/**
 * /honorarios/cotizaciones — Listado + filtros + semáforo vigencia.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import {
    PRODUCTO_LABEL, COT_ESTADOS, fmtMXNDec, fmtDateCot,
    diasVigencia, colorVigencia,
} from "./_common";
import { PRODUCTOS } from "../aceptacion/_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

const ESTADOS_FILTRO = ["borrador", "enviada", "aprobada", "aceptada_cliente", "rechazada", "vencida"];

export default function CotizacionesPage() {
    const [cots, setCots] = useState([]);
    const [filtroEstado, setFiltroEstado] = useState("");
    const [filtroProd, setFiltroProd] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const params = {};
        if (filtroEstado && filtroEstado !== "all") params.estado = filtroEstado;
        if (filtroProd && filtroProd !== "all") params.producto = filtroProd;
        api.get("/honorarios/cotizaciones", { params })
            .then((r) => setCots(r.data || []))
            .finally(() => setLoading(false));
    }, [filtroEstado, filtroProd]);

    return (
        <div className="space-y-6" data-testid="cotizaciones-page">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Cotizaciones
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Historial completo con semáforo de vigencia (≤5d rojo · ≤15d ámbar).
                    </p>
                </div>
                <Button asChild data-testid="btn-nueva-cotizacion">
                    <Link to="/honorarios/cotizador">+ Nueva cotización</Link>
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">Filtros</CardTitle>
                </CardHeader>
                <CardContent className="flex gap-4 flex-wrap">
                    <div className="w-56">
                        <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                            <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                {ESTADOS_FILTRO.map((e) => (
                                    <SelectItem key={e} value={e}>{COT_ESTADOS[e]?.label || e}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-56">
                        <Select value={filtroProd} onValueChange={setFiltroProd}>
                            <SelectTrigger><SelectValue placeholder="Producto" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los productos</SelectItem>
                                {PRODUCTOS.map((p) => (
                                    <SelectItem key={p.code} value={p.code}>{p.code}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Cliente</th>
                                <th className="px-3 py-2 text-left">Producto</th>
                                <th className="px-3 py-2 text-right">Total Año 1</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-center">Vigencia</th>
                                <th className="px-3 py-2 text-right">Creada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Cargando…</td></tr>}
                            {!loading && cots.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Sin cotizaciones</td></tr>}
                            {cots.map((c) => {
                                const dias = diasVigencia(c.vence_at);
                                const estadoCfg = COT_ESTADOS[c.estado] || { label: c.estado, cls: "" };
                                return (
                                    <tr key={c.id} className="border-t hover:bg-muted/30"
                                        data-testid={`cot-row-${c.folio_cotizacion || c.id}`}>
                                        <td className="px-3 py-2">
                                            <Link to={`/honorarios/cotizaciones/${c.folio_cotizacion || c.id}`}
                                                className="font-mono text-xs text-primary hover:underline">
                                                {c.folio_cotizacion || `(borrador ${c.id.slice(0, 6)})`}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2">{c.cliente_prospecto?.razon_social || "—"}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {c.producto} · {c.producto_nombre || PRODUCTO_LABEL[c.producto]}
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold">
                                            {fmtMXNDec(c.resultado?.ano_1_con_iva?.propuesto)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Badge className={estadoCfg.cls}>{estadoCfg.label}</Badge>
                                            {c.requiere_aprobacion_dg && !c.aprobada_por_dg && (
                                                <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-300">DG</Badge>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            {c.vence_at ? (
                                                <Badge className={colorVigencia(dias)}>
                                                    {dias > 0 ? `${dias}d` : "Vencida"}
                                                </Badge>
                                            ) : <span className="text-xs text-muted-foreground">—</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmtDateCot(c.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
